import { useState } from "react";
import { Link } from "react-router-dom";
import { LucideClock, LucideArrowRight } from "lucide-react";
import { pdfjs } from "react-pdf";
import { PDFPreview } from "@/reports/report_components/PDFPreview";
import { ReportFormHeader } from "@/reports/report_components/ReportFormHeader";
import { Button } from "@/components/ui/button";
import { REPORT_TYPES } from "@/constants/reportTypes";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url,
).toString();

const ENABLED = REPORT_TYPES.filter((t) => !t.disabled);

const GROUPS = [
  { label: "Kindergarten", types: REPORT_TYPES.filter((t) => t.grades?.includes("K")) },
  { label: "Grades 1–6",  types: REPORT_TYPES.filter((t) => t.grades?.includes(1)) },
  { label: "Grades 7–8",  types: REPORT_TYPES.filter((t) => t.grades?.includes(7)) },
  { label: "Grades 9–12", types: REPORT_TYPES.filter((t) => t.grades?.includes(9)) },
];

export default function DemoPicker() {
  const [selected, setSelected] = useState(ENABLED[0] ?? null);

  return (
    <div className="flex flex-col h-[calc(100dvh-60px)]">
      <ReportFormHeader
        titleOnly
        title="Demo"
        subtitle="Choose a report card, then try it for free - no account needed."
      />

      <div className="flex flex-1 overflow-hidden max-w-[1080px] w-full mx-auto border-x border-border">
        {/* ── LEFT - card list ──────────────────────────────── */}
        <div className="w-[360px] shrink-0 border-r border-border px-4 py-5 flex flex-col">
          <div className="flex flex-col gap-5 flex-1 overflow-y-auto">
            {GROUPS.map((group) => (
              <div key={group.label}>
                <p className="text-[0.6875rem] font-semibold text-muted-foreground uppercase tracking-wider mb-2 px-1">
                  {group.label}
                </p>
                <div className="grid grid-cols-2 gap-1.5">
                  {group.types.map((type) => {
                    const isSelected = selected?.value === type.value;
                    return (
                      <button
                        key={type.value}
                        type="button"
                        disabled={type.disabled}
                        onClick={() => !type.disabled && setSelected(type)}
                        className={`w-full text-left rounded-xl px-4 py-3 border bg-card text-card-foreground dark:bg-card-gradient transition-[border-color,box-shadow,transform] duration-150 ${
                          type.disabled
                            ? "opacity-40 cursor-not-allowed border-border"
                            : isSelected
                            ? "border-primary shadow-card-hover -translate-y-px cursor-pointer"
                            : "border-border border-t-highlight shadow-card hover:border-primary hover:shadow-card-hover hover:-translate-y-px cursor-pointer"
                        }`}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 mb-0.5">
                              <span className="text-sm font-semibold text-foreground">{type.demoLabel ?? type.label}</span>
                              {type.disabled && (
                                <span className="inline-flex items-center gap-1 text-[0.6875rem] text-muted-foreground/70 bg-muted/80 rounded-[3px] px-[0.35rem] py-[0.1rem]">
                                  <LucideClock size={9} />
                                  Coming soon
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground leading-[1.45] line-clamp-2 m-0">
                              {type.description}
                            </p>
                          </div>
                          {isSelected && !type.disabled && (
                            <div className="w-2 h-2 rounded-full bg-primary shrink-0" />
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Single CTA pinned at bottom */}
          <div className="pt-6 shrink-0">
            {selected && !selected.disabled ? (
              <Button asChild className="w-full">
                <Link to={selected.demoPath}>
                  Try this report
                  <LucideArrowRight size={14} />
                </Link>
              </Button>
            ) : (
              <Button disabled className="w-full">
                Try this report
                <LucideArrowRight size={14} />
              </Button>
            )}
          </div>
        </div>

        {/* ── RIGHT - PDF preview ───────────────────────────── */}
        <div className="flex-1 overflow-hidden">
          <PDFPreview
            src={selected?.pdfPreview ?? null}
            defaultPageCount={2}
          />
        </div>
      </div>
    </div>
  );
}
